import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  getUser, activateUser, deactivateUser, archiveUser, resetUserPassword,
} from '../../services/userService'
import { getBranches } from '../../services/branchService'
import { getRoles } from '../../services/referenceService'
import type { UserResponse, BranchResponse, Role } from '../../types'
import { useAdminAuth } from '../../context/AdminAuthContext'
import { RoleBadge, UserStatusDot } from '../UsersPage/UsersPage'
import UserFormModal from '../../components/UserForm/UserFormModal'
import './UserDetailPage.css'

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { me } = useAdminAuth()

  const [user, setUser] = useState<UserResponse | null>(null)
  const [branches, setBranches] = useState<BranchResponse[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null)

  const isSelf = me?.id === id

  const load = useCallback(async () => {
    if (!id) return
    try {
      const u = await getUser(id)
      setUser(u)
    } catch (err) {
      console.error('[ERROR] Failed to load user', err)
      showToast(t('admin.user_detail.load_failed'), 'error')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    load()
    getBranches().then(setBranches).catch(() => {})
    getRoles().then(setRoles).catch(() => {})
  }, [load])

  function showToast(msg: string, type: 'success' | 'error') {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3500)
  }

  async function doAction(fn: () => Promise<void>, successMsg: string, errorMap?: Record<string, string>) {
    setActionLoading(true)
    try {
      await fn()
      await load()
      showToast(successMsg, 'success')
    } catch (err: any) {
      const code = err?.response?.data?.errorCode
      const msg = (errorMap && errorMap[code]) || err?.response?.data?.message || t('admin.user_detail.action_failed')
      showToast(msg, 'error')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleArchive() {
    if (!user || !confirm(t('admin.user_detail.archive_confirm', { login: user.login }))) return
    setActionLoading(true)
    try {
      await archiveUser(user.id)
      showToast(t('admin.user_detail.archived'), 'success')
      setTimeout(() => navigate('/admin/users'), 1200)
    } catch (err: any) {
      const code = err?.response?.data?.errorCode
      if (code === 'USER_HAS_ACTIVE_ORDERS') showToast(t('admin.user_detail.has_active_orders'), 'error')
      else if (code === 'CANNOT_ARCHIVE_SELF') showToast(t('admin.user_detail.cannot_archive_self'), 'error')
      else showToast(t('admin.user_detail.archive_failed'), 'error')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleResetPassword() {
    if (!user || !confirm(t('admin.user_detail.reset_confirm', { email: user.email }))) return
    await doAction(() => resetUserPassword(user.id), t('admin.user_detail.password_reset_sent'))
  }

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}><div className="admin-spinner" /></div>
  }

  if (!user) {
    return <div style={{ color: '#5a5d72', padding: 32 }}>{t('admin.user_detail.not_found')}</div>
  }

  const isArchived = !!user.archivedAt

  return (
    <div className="user-detail">
      <div className="admin-page-header">
        <button className="admin-back-btn" onClick={() => navigate('/admin/users')}>
          <BackIcon />
        </button>
        <h1 className="user-detail__title">{user.firstName} {user.lastName}</h1>
        <RoleBadge role={user.role} />
        <UserStatusDot verified={user.verified} archived={isArchived} />
      </div>

      {/* Actions */}
      {!isArchived && (
        <div className="user-detail__actions">
          {user.verified ? (
            <button
              className="admin-btn admin-btn--warn"
              disabled={actionLoading || isSelf}
              title={isSelf ? t('admin.user_detail.cannot_deactivate_self') : ''}
              onClick={() => doAction(() => deactivateUser(user.id), t('admin.user_detail.deactivated'), {
                CANNOT_DEACTIVATE_SELF: t('admin.user_detail.cannot_deactivate_self'),
              })}
            >
              {t('admin.user_detail.deactivate')}
            </button>
          ) : (
            <button
              className="admin-btn admin-btn--primary"
              disabled={actionLoading}
              onClick={() => doAction(() => activateUser(user.id), t('admin.user_detail.activated'))}
            >
              {t('admin.user_detail.activate')}
            </button>
          )}

          <button
            className="admin-btn admin-btn--secondary"
            disabled={actionLoading}
            onClick={handleResetPassword}
          >
            {t('admin.user_detail.reset_password')}
          </button>

          <button
            className="admin-btn admin-btn--secondary"
            disabled={actionLoading}
            onClick={() => setShowEdit(true)}
          >
            {t('admin.user_detail.edit')}
          </button>

          <button
            className="admin-btn admin-btn--danger"
            disabled={actionLoading || isSelf}
            title={isSelf ? t('admin.user_detail.cannot_archive_self') : ''}
            onClick={handleArchive}
          >
            {t('admin.user_detail.archive')}
          </button>
        </div>
      )}

      {/* Info */}
      <div className="order-detail__grid-2">
        <div className="admin-detail-card">
          <p className="admin-detail-card__title">{t('admin.user_detail.card_profile')}</p>
          <div className="admin-detail-grid">
            <Field label={t('admin.user_detail.field_first_name')} value={user.firstName} />
            <Field label={t('admin.user_detail.field_last_name')} value={user.lastName} />
            <Field label={t('admin.user_detail.field_login')} value={user.login} />
            <Field label={t('admin.user_detail.field_email')} value={user.email} />
            <Field label={t('admin.user_detail.field_phone')} value={user.phoneNumber} />
            <Field label={t('admin.user_detail.field_role')} value={user.role} />
          </div>
        </div>

        <div className="admin-detail-card">
          <p className="admin-detail-card__title">{t('admin.user_detail.card_account')}</p>
          <div className="admin-detail-grid">
            <Field label={t('admin.user_detail.field_branch')} value={user.branchName} />
            <Field label={t('admin.user_detail.field_created')} value={formatDate(user.createdAt)} />
            <Field label={t('admin.user_detail.field_verified')} value={user.verified ? t('admin.user_detail.yes') : t('admin.user_detail.no')} highlight={user.verified ? 'ok' : 'warn'} />
            <Field label={t('admin.user_detail.field_email_verified')} value={user.emailVerified ? t('admin.user_detail.yes') : t('admin.user_detail.no')} />
            {user.archivedAt && <Field label={t('admin.user_detail.field_archived_at')} value={formatDate(user.archivedAt)} />}
          </div>
        </div>
      </div>

      {showEdit && (
        <UserFormModal
          user={user}
          branches={branches}
          roles={roles}
          onClose={() => setShowEdit(false)}
          onSaved={(updated) => { setUser(updated); setShowEdit(false); showToast(t('admin.user_detail.updated'), 'success') }}
        />
      )}

      {toast && <div className={`admin-toast admin-toast--${toast.type}`}>{toast.msg}</div>}
    </div>
  )
}

function Field({ label, value, highlight }: { label: string; value?: string | null; highlight?: 'ok' | 'warn' | 'error' }) {
  const color = highlight === 'ok' ? '#10b981' : highlight === 'warn' ? '#f59e0b' : highlight === 'error' ? '#ef4444' : undefined
  return (
    <div className="admin-detail-field">
      <span className="admin-detail-field__label">{label}</span>
      <span className="admin-detail-field__value" style={color ? { color } : undefined}>{value || '—'}</span>
    </div>
  )
}

function BackIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M11 4L6 9L11 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
